import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import CardHeader from '@mui/material/CardHeader';
import { useWidgetData } from './useWidgetData';
import { WidgetConfig } from './widget';
import { WidgetEditControls } from './WidgetEditControls';

interface TableWidgetProps {
  widget: WidgetConfig;
  onEdit: (widget: WidgetConfig) => void;
  onRemove: (widgetId: string) => void;
  onSave?: (widget: WidgetConfig) => void;
  hasUnsavedChanges?: boolean;
  isEditing: boolean;
}

function getStatusColor(status: string) {
  if (status === 'Active') return { color: 'success', label: 'Active' };
  if (status === 'Inactive') return { color: 'error', label: 'Inactive' };
  return { color: 'warning', label: status };
}

export const TableWidget = ({
  widget,
  onEdit,
  onRemove,
  onSave,
  hasUnsavedChanges,
  isEditing,
}: TableWidgetProps) => {
  const { data, isLoading, error, refetch } = useWidgetData(widget);

  const tableData = React.useMemo(() => {
    let rawData: any[] = [];

    if (widget.data) {
      rawData = widget.data;
    } else if (data?.data?.Results && Array.isArray(data.data.Results)) {
      rawData = data.data.Results;
    } else if (data?.data && Array.isArray(data.data)) {
      rawData = data.data;
    }

    const cleanedData = rawData
      .filter((row: any) => {
        if (!row || typeof row !== 'object') return false;

        const values = Object.values(row).filter(
          (value) =>
            value !== null &&
            value !== undefined &&
            value !== '' &&
            value !== 'telemetry' &&
            String(value).trim() !== ''
        );

        return values.length > 0;
      })
      .map((row: any) => {
        const cleanedRow: any = {};
        Object.keys(row).forEach((key) => {
          if (key !== 'originalTelemetry') {
            let value = row[key];

            if (value === '' || value === 'telemetry') {
              value = null;
            }

            cleanedRow[key] = value;
          }
        });

        return cleanedRow;
      });

    const selectedColumns =
      widget.chartConfiguration.selectedColumns ||
      widget.chartConfiguration.dataSeries;

    if (selectedColumns && selectedColumns.length > 0) {
      const selectedColumnNames = selectedColumns.map((col) => col.columnName);

      return cleanedData
        .map((row: any) => {
          const filteredRow: any = {};
          selectedColumnNames.forEach((columnName) => {
            if (Object.prototype.hasOwnProperty.call(row, columnName)) {
              filteredRow[columnName] = row[columnName];
            }
          });
          return filteredRow;
        })
        .filter((row: any) => {
          const values = Object.values(row).filter(
            (value) =>
              value !== null &&
              value !== undefined &&
              value !== '' &&
              String(value).trim() !== ''
          );
          return values.length > 0;
        })
        .filter((row: any) =>
          selectedColumnNames.some(
            (columnName) =>
              row[columnName] !== null &&
              row[columnName] !== undefined &&
              row[columnName] !== '' &&
              String(row[columnName]).trim() !== ''
          )
        );
    }

    return cleanedData;
  }, [
    widget.data,
    data,
    widget.chartConfiguration.selectedColumns,
    widget.chartConfiguration.dataSeries,
  ]);

  const allAvailableColumns = React.useMemo(() => {
    const selectedColumns =
      widget.chartConfiguration.selectedColumns ||
      widget.chartConfiguration.dataSeries;

    if (selectedColumns && selectedColumns.length > 0) {
      const columnNames = selectedColumns.map((col) => col.columnName);
      return columnNames;
    }

    if (tableData.length > 0) {
      const allColumns = Object.keys(tableData[0]).filter(
        (key) =>
          key !== 'originalTelemetry' &&
          key !== 'telemetry' &&
          key.trim() !== '' &&
          key !== 'undefined' &&
          key !== 'null'
      );
      const filteredColumns = allColumns.filter((column) => {
        const nonNullCount = tableData.filter((row) => {
          const value = row[column];
          return (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            String(value).trim() !== ''
          );
        }).length;

        const threshold = Math.max(1, Math.floor(tableData.length * 0.2));
        return nonNullCount >= threshold;
      });
      return filteredColumns;
    }
    return [];
  }, [
    tableData,
    widget.chartConfiguration.selectedColumns,
    widget.chartConfiguration.dataSeries,
  ]);

  const columns = React.useMemo(
    () => allAvailableColumns,
    [allAvailableColumns]
  );

  const getColumnLabel = (columnName: string) => {
    const selectedColumns =
      widget.chartConfiguration.selectedColumns ||
      widget.chartConfiguration.dataSeries;

    if (selectedColumns) {
      const selectedColumn = selectedColumns.find(
        (col) => col.columnName === columnName
      );
      if (selectedColumn && selectedColumn.label) {
        return selectedColumn.label;
      }
    }

    return columnName;
  };

  const renderCellValue = (value: any, columnName: string) => {
    if (value === null || value === undefined) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'True' : 'False'}
          size="small"
          color={value ? 'success' : 'error'}
          sx={{ fontWeight: 500 }}
        />
      );
    }

    if (typeof value === 'number') {
      return (
        <Typography
          variant="body2"
          sx={{ color: 'primary.main', fontWeight: 500 }}
        >
          {value.toLocaleString()}
        </Typography>
      );
    }

    if (
      typeof value === 'string' &&
      (value.includes('T') || value.includes('-')) &&
      !Number.isNaN(Date.parse(value))
    ) {
      return (
        <Typography variant="body2">
          {new Date(value).toLocaleString()}
        </Typography>
      );
    }

    if (
      columnName.toLowerCase().includes('status') ||
      columnName.toLowerCase().includes('state')
    ) {
      return (
        <Chip
          label={value}
          size="small"
          color={getStatusColor(value).color as any}
          sx={{ fontWeight: 500 }}
        />
      );
    }

    return <Typography variant="body2">{String(value)}</Typography>;
  };

  return (
    <Box position="relative" height="100%">
      <WidgetEditControls
        widget={widget}
        onEdit={onEdit}
        onRemove={onRemove}
        onSave={onSave}
        hasUnsavedChanges={hasUnsavedChanges}
        isVisible={isEditing}
      />

      <Card
        sx={{
          height: '100%',
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: 1,
          borderColor: 'divider',
          boxShadow: 2,
        }}
      >
        <CardHeader
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            pb: 1,
          }}
          title={
            <Typography variant="h6" color="text.primary">
              {widget.title}
            </Typography>
          }
          action={
            <Box display="flex" alignItems="center" gap={1}>
              {isLoading && (
                <CircularProgress size={20} sx={{ color: 'primary.light' }} />
              )}
              <button
                onClick={() => refetch()}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <AutorenewIcon sx={{ fontSize: 20 }} />
              </button>
            </Box>
          }
        />
        <CardContent
          sx={{
            pt: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {error && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={128}
              color="error.main"
            >
              <ErrorOutlineIcon sx={{ fontSize: 32, mr: 1 }} />
              <span>Error loading data</span>
            </Box>
          )}

          {!error && tableData.length === 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flex={1}
              color="text.secondary"
            >
              <Typography variant="body2">
                {(() => {
                  const selectedColumns =
                    widget.chartConfiguration.selectedColumns ||
                    widget.chartConfiguration.dataSeries;
                  return selectedColumns && selectedColumns.length > 0
                    ? `No data available for the selected columns (${selectedColumns
                      .map((col) => col.label || col.columnName)
                      .join(
                        ', '
                      )}). Try selecting different columns or execute a different query.`
                    : 'No data available. Execute a query to populate the table.';
                })()}
              </Typography>
            </Box>
          )}

          {!error && tableData.length > 0 && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <TableContainer
                sx={{
                  flex: 1,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 150,
                  maxHeight: 300,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    },
                  },
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column}
                          sx={{
                            color: 'text.primary',
                            fontSize: '18px',
                            fontWeight: 600,
                            backgroundColor: 'grey.50',
                            borderBottom: 2,
                            borderColor: 'divider',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getColumnLabel(column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row: any, index: number) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        {columns.map((column) => (
                          <TableCell
                            key={column}
                            sx={{
                              color: 'text.primary',
                              py: 1,
                              fontSize: '0.8rem',
                              minWidth: 120,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {renderCellValue(row[column], column)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>
              Type: {widget.chartType} | Refresh:{' '}
              {widget.dataSource.refreshRate}s | Last updated:{' '}
              {data?.timestamp
                ? new Date(data.timestamp).toLocaleTimeString()
                : 'Never'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
